<template>
  <div class="login-page">
    <div class="login-card">
      <v-img src="@/assets/logo2.png" alt="Logo" class="logo-image" />
      <h2 class="welcome-text">Login</h2>
      <p class="description-text">Sign in to your account</p>
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="email"
              label="Email"
              variant="solo"
              rounded
              flat="true"
              bg-color="#f5f5f5"
              hide-details
              prepend-inner-icon="mdi-email"
              :rules="[
                v => !!v || 'Email is required',
                v => /.+@.+\..+/.test(v) || 'Must be a valid email'
              ]"
   
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              label="Password"
              variant="solo"
              rounded
              flat="true"
              bg-color="#f5f5f5"
              hide-details
              prepend-inner-icon="mdi-lock"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword"
              :rules="[v => !!v || 'Password is required']"
            
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-btn
              rounded
              dark
              block
              @click="login"
              :loading="loading"
              :disabled="!valid || loading"
              class="login-btn"
            >
              {{ loading ? 'Signing in...' : 'Login' }}
            </v-btn>
          </v-col>
          <v-col cols="12" class="text-center">
            <a 
              href="#" 
              @click.prevent="showForgotPasswordDialog = true"
              class="forgot-password-link"
            >
              Forgot Password?
            </a>
          </v-col>
        </v-row>
      </v-form>
    </div>
    
    <!-- Notification Dialog - Outside card to cover entire page -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <!-- Password Reset Dialog -->
    <PasswordResetDialog
      v-model:visible="showPasswordResetDialog"
      @password-changed="handlePasswordChange"
    />

    <!-- Forgot Password Dialog -->
    <ForgotPasswordDialog
      v-model:visible="showForgotPasswordDialog"
      @reset-email-sent="handleResetEmailSent"
    />
  </div>
</template>

<script>
import { signInWithEmailAndPassword, updatePassword } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import NotificationDialog from '../components/NotificationDialog.vue';
import PasswordResetDialog from '../components/PasswordResetDialog.vue';
import ForgotPasswordDialog from '../components/ForgotPasswordDialog.vue';

export default {
  components: {
    NotificationDialog,
    PasswordResetDialog,
    ForgotPasswordDialog
  },
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      valid: false,
      loading: false,
      showNotification: false,
      notificationType: 'error',
      notificationTitle: '',
      notificationMessage: '',
      showPasswordResetDialog: false,
      showForgotPasswordDialog: false,
      currentUser: null,
    };
  },
  methods: {
    async login() {
      if (!this.$refs.form.validate()) return;
      
      this.showNotification = false;
      this.loading = true;
      
      try {
        // Sign in with Firebase Auth
        const userCredential = await signInWithEmailAndPassword(auth, this.email, this.password);
        const user = userCredential.user;
        
        // Check if user has temporary password and verify status
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Check if user is active first
          if (userData.status !== 'active') {
            await auth.signOut();
            throw new Error('User account is inactive');
          }
          
          if (userData.hasTemporaryPassword) {
            // Store current user and show password reset dialog
            this.currentUser = user;
            this.showPasswordResetDialog = true;
            return; // Don't proceed with normal login flow
          }
        }
        
        // Fetch user data to get role and check status
        try {
          await this.$store.dispatch('fetchUserData', user.uid);
        } catch (error) {
          // If user is inactive, sign them out and show error
          if (error.message === 'User account is inactive') {
            await auth.signOut();
            throw new Error('User account is inactive');
          }
          throw error;
        }
        
        // Log login activity
        const displayName = user.displayName || user.email.split('@')[0];
        await ActivityLogger.login(displayName, user.uid, 'Login Page');
        
        // Show success notification
        this.notificationType = 'success';
        this.notificationTitle = 'Welcome!';
        this.notificationMessage = 'Login successful! Redirecting to dashboard...';

        
        // Delay navigation to show success message
        setTimeout(() => {
          // Role-based redirection
          const userRole = this.$store.getters.userRole;
          
          if (userRole === 'Executive') {
            // Executive users go to dashboard with full access
            this.$router.push({ name: 'Dashboard' });
          } else if (userRole === 'Admin') {
            // Admin users go to dashboard with restricted access
            this.$router.push({ name: 'Dashboard' });
          } else if (userRole === 'Employee') {
            // Employees go to dashboard with client access
            this.$router.push({ name: 'Dashboard' });
          } else if (userRole === 'QA Agent') {
            // QA Agents go directly to QA page
            this.$router.push({ name: 'Qa' });
          } else {
            // Fallback to dashboard
            this.$router.push({ name: 'Dashboard' });
          }
        }, 1500);
        
        return; // Prevent immediate navigation
        
      } catch (error) {
        console.error('Login error:', error);
        
        // Log failed login attempt
        await ActivityLogger.loginFailed(this.email, error.message);
        
        // Show error notification
        this.notificationType = 'error';
        this.notificationTitle = 'Login Failed!';
        
        if (error.code === 'auth/user-not-found') {
          this.notificationMessage = 'No account found with this email address.';
        } else if (error.code === 'auth/wrong-password') {
          this.notificationMessage = 'Incorrect password.';
        } else if (error.code === 'auth/invalid-email') {
          this.notificationMessage = 'Please enter a valid email address.';
        } else if (error.code === 'auth/user-disabled') {
          this.notificationMessage = 'This account has been disabled.';
        } else if (error.message === 'User account is inactive') {
          this.notificationMessage = 'Your account is inactive. Please contact an administrator.';
        } else {
          this.notificationMessage = 'Invalid email or password.';
        }
        
        this.showNotification = true;
      } finally {
        this.loading = false;
      }
    },

    async handlePasswordChange({ newPassword, confirmPassword }) {
      if (!this.currentUser) return;
      
      try {
        // Update Firebase Auth password
        await updatePassword(this.currentUser, newPassword);
        
        // Update Firestore to remove temporary password flag
        await updateDoc(doc(db, 'users', this.currentUser.uid), {
          hasTemporaryPassword: false,
          temporaryPassword: null,
          passwordChangedAt: new Date().toISOString()
        });
        
        // Fetch user data to get role and check status
        try {
          await this.$store.dispatch('fetchUserData', this.currentUser.uid);
        } catch (error) {
          // If user is inactive, sign them out and show error
          if (error.message === 'User account is inactive') {
            await auth.signOut();
            throw new Error('User account is inactive');
          }
          throw error;
        }
        
        // Log password change activity
        const displayName = this.currentUser.displayName || this.currentUser.email.split('@')[0];
        await ActivityLogger.login(displayName, this.currentUser.uid);
        
        // Show success notification
        this.notificationType = 'success';
        this.notificationTitle = 'Password Updated!';
        this.notificationMessage = 'Your password has been updated successfully! Redirecting to dashboard...';
        this.showNotification = true;
        
        // Delay navigation to show success message
        setTimeout(() => {
          // Role-based redirection
          const userRole = this.$store.getters.userRole;
          
          if (userRole === 'Executive') {
            // Executive users go to dashboard with full access
            this.$router.push({ name: 'Dashboard' });
          } else if (userRole === 'Admin') {
            // Admin users go to dashboard with restricted access
            this.$router.push({ name: 'Dashboard' });
          } else if (userRole === 'Employee' || userRole === 'QA Agent') {
            // Regular users go to dashboard
            this.$router.push({ name: 'Dashboard' });
          } else {
            // Fallback to dashboard
            this.$router.push({ name: 'Dashboard' });
          }
        }, 1500);
        
      } catch (error) {
        console.error('Error updating password:', error);
        
        // Show error notification
        this.notificationType = 'error';
        this.notificationTitle = 'Password Update Failed!';
        
        if (error.message === 'User account is inactive') {
          this.notificationMessage = 'Your account is inactive. Please contact an administrator.';
        } else {
          this.notificationMessage = 'Failed to update password. Please try again.';
        }
        
        this.showNotification = true;
      }
    },

    handleResetEmailSent(result) {
      this.notificationType = result.success ? 'success' : 'error';
      this.notificationTitle = result.success ? 'Email Sent!' : 'Error!';
      this.notificationMessage = result.message;
      this.showNotification = true;
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.login-page {
  min-height: 100vh;
  background: url('~@/assets/bg3.png') no-repeat center center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-card {
  background: rgba(255, 255, 255, 0.85);

  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 28px;
  padding: 48px 40px 40px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 370px;
  max-width: 420px;
  width: 100%;
}

.logo-image {
  width: 110px;
  margin-bottom: 18px;
}

.welcome-text {
  font-weight: 700;
  margin-bottom: 6px;
  color: #002060;
  font-size: 2rem;
}

.description-text {
  color: #444;
  margin-bottom: 28px;
  font-size: 1.1rem;
}

.login-input {
  width: 100%;
  margin-bottom: 0;
  font-size: 1.1rem;
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.login-btn {
  background: #002060;
  color: #fff;
  font-weight: 700;
  font-size: 1.1rem;
  margin-top: 8px;
  height: 48px;
  letter-spacing: 0.5px;
}

.forgot-password-link {
  color: #002060;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.forgot-password-link:hover {
  color: #1a2d42;
  text-decoration: underline;
}
</style>
